/**
 * Flash Sale Bar
 * - Live countdown from a shop-timezone Unix timestamp (data-end)
 * - Optional dismiss with localStorage persistence
 * - Optional hide / custom message when expired
 */
if (!customElements.get('flash-sale-bar')) {
  class FlashSaleBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.storageKey = this.dataset.storageKey || `flash-sale-bar:${this.id}`;
      this.hideWhenExpired = this.dataset.hideWhenExpired === 'true';
      this.endTimestamp = Number(this.dataset.end);
      this.liveRegion = this.querySelector('[data-fsb-live-region]');
      this.liveGroup = this.querySelector('[data-fsb-live]');
      this.expiredGroup = this.querySelector('[data-fsb-expired]');
      this.hoursEl = this.querySelector('[data-fsb-hours]');
      this.minutesEl = this.querySelector('[data-fsb-minutes]');
      this.secondsEl = this.querySelector('[data-fsb-seconds]');
      this.dismissBtn = this.querySelector('[data-fsb-dismiss]');

      if (this.isDismissed()) {
        this.hideBar(true);
        return;
      }

      this.dismissBtn?.addEventListener('click', () => this.dismiss());

      if (!Number.isFinite(this.endTimestamp) || this.endTimestamp <= 0) {
        this.handleExpired();
        return;
      }

      this.tick();
      this.timerId = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.timerId) window.clearInterval(this.timerId);
    }

    isDismissed() {
      try {
        return window.localStorage.getItem(this.storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

    dismiss() {
      try {
        window.localStorage.setItem(this.storageKey, '1');
      } catch (e) {
        // Private mode / blocked storage — still hide for this page view
      }
      this.hideBar(true);
      this.classList.add('is-dismissed');
    }

    hideBar(fromDismiss) {
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      if (this.timerId) window.clearInterval(this.timerId);
      if (fromDismiss && this.liveRegion) {
        this.liveRegion.textContent = 'Announcement dismissed';
      }
    }

    tick() {
      const remainingMs = this.endTimestamp * 1000 - Date.now();

      if (remainingMs <= 0) {
        this.handleExpired();
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setDigits(this.hoursEl, hours);
      this.setDigits(this.minutesEl, minutes);
      this.setDigits(this.secondsEl, seconds);

      // Update SR text about once a minute to avoid noisy announcements
      if (!this.lastAnnouncedMinute || this.lastAnnouncedMinute !== minutes + hours * 60) {
        this.lastAnnouncedMinute = minutes + hours * 60;
        if (this.liveRegion) {
          this.liveRegion.textContent = `Sale ends in ${hours} hours, ${minutes} minutes, ${seconds} seconds`;
        }
      }
    }

    setDigits(el, value) {
      if (!el) return;
      el.textContent = String(value).padStart(2, '0');
    }

    handleExpired() {
      if (this.timerId) window.clearInterval(this.timerId);

      if (this.hideWhenExpired) {
        this.hideBar(false);
        return;
      }

      if (this.liveGroup) this.liveGroup.hidden = true;
      if (this.expiredGroup) this.expiredGroup.hidden = false;

      if (this.liveRegion) {
        this.liveRegion.textContent = this.dataset.expiredMessage || 'This sale has ended';
      }
    }
  }

  customElements.define('flash-sale-bar', FlashSaleBar);
}
